import type {
  HubDeletedResource,
  HubResource,
} from '@robovision/hub'
import {
  buildObject,
  filterObject,
  HttpStatusCode,
} from '@robovision/rvai-utils'
import { defineStore } from 'pinia'
import {
  reactive,
  toRefs,
} from 'vue'
import { hubApi } from '~services/api'
import { queryClient } from '~services/query-client'
import { RETHROW_ERROR_AFTER_HANDLING } from '~utils/api/error-handling'


interface ResourceFilters {
  definitionId?: string
  projectId?: string
  sourceJobId?: string
}

interface ResourcesState {
  blobs: Record<string, HubResource>
  models: Record<string, HubResource>
}

export const useResourcesStore = defineStore('resources', () => {
  const state = reactive<ResourcesState>({
    blobs: {},
    models: {},
  }) as ResourcesState

  const getters = {
    getBlobResource (resourceId: string): HubResource | undefined {
      return state.blobs[resourceId]
    },

    getModelResource (resourceId: string): HubResource | undefined {
      const model = state.models[resourceId]
      return model?.valid ? model : undefined
    },

    getModelResourcesFiltered ({
      definitionId,
      projectId,
      sourceJobId,
    }: ResourceFilters): HubResource[] {
      if (!definitionId && !projectId && !sourceJobId) return []

      return Object.values(state.models)
        .filter(m =>
          (!definitionId || m.definitionId === definitionId) &&
          (!projectId || m.projectId === projectId) &&
          (!sourceJobId || m.sourceJobId === sourceJobId) &&
          m.valid)
    },

    getBlobResourcesFiltered ({
      definitionId,
      projectId,
      sourceJobId,
    }: ResourceFilters): HubResource[] {
      if (!definitionId && !projectId && !sourceJobId) return []

      return Object.values(state.blobs)
        .filter(b =>
          (!definitionId || b.definitionId === definitionId) &&
          (!projectId || b.projectId === projectId) &&
          (!sourceJobId || b.sourceJobId === sourceJobId) &&
          b.valid)
    },
  }

  const actions = {
    deleteModelBySourceJobId (sourceJobId: string): void {
      state.models = filterObject(state.models, (m) => m.sourceJobId !== sourceJobId)
    },

    handleDeleteResource (deletedResources: HubDeletedResource) {
      if (state.models[deletedResources.id]) {
        delete state.models[deletedResources.id]
      }
      if (state.blobs[deletedResources.id]) {
        delete state.blobs[deletedResources.id]
      }
    },

    async loadModel (resourceId: string, allowNotFound?: boolean): Promise<void> {
      const { data } = await queryClient.fetchQuery({
        queryFn: () => hubApi.resources.getModelResourceInfo(resourceId, allowNotFound ? {
          ...RETHROW_ERROR_AFTER_HANDLING,
          validateStatus: status => [HttpStatusCode.OK, HttpStatusCode.NOT_FOUND].includes(status),
        } : RETHROW_ERROR_AFTER_HANDLING),
        queryKey: ['getModelResourceInfo', resourceId],
      })

      state.models = {
        ...state.models,
        [data.id]: data,
      }
    },

    async loadModels ({
      definitionId,
      jobId,
      projectId,
    }: {
      definitionId?: string
      jobId?: string
      projectId?: string
    } = {}): Promise<string[]> {
      const { data } = await queryClient.fetchQuery({
        queryFn: () => hubApi.resources.listModelResources({
          filter: [
            (definitionId && `definitionId:${definitionId}`),
            (jobId && `sourceJobId:${jobId}`),
            (projectId && `projectId:${projectId}`),
          ].filter(Boolean),
        }, RETHROW_ERROR_AFTER_HANDLING),
        queryKey: ['listModelResources', definitionId, jobId, projectId],
      })

      state.models = {
        ...state.models,
        ...buildObject(data.items, r => r.id, r => r),
      }

      return data.items.map(r => r.id)
    },

    async renameModel (resourceId: string, name: string): Promise<void> {
      const model = state.models[resourceId]
      const { data } = await hubApi.resources.overrideModelResourceMetadata(resourceId, {
        metadata: model?.metadata,
        name,
      })
      state.models = {
        ...state.models,
        [data.id]: data,
      }
    },

    async renameBlob (resourceId: string, name: string): Promise<void> {
      const blob = state.blobs[resourceId]
      const { data } = await hubApi.resources.overrideBlobResourceMetadata(resourceId, {
        metadata: blob?.metadata,
        name,
      })
      state.blobs = {
        ...state.blobs,
        [data.id]: data,
      }
    },

    setBlobs (resources: HubResource[]) {
      state.blobs = {
        ...state.blobs,
        ...buildObject(resources, r => r.id, r => r),
      }
    },

    setModels (resources: HubResource[]) {
      state.models = {
        ...state.models,
        ...buildObject(resources, r => r.id, r => r),
      }
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },
  }

  return {
    ...toRefs(state),
    ...getters,
    ...actions,
  }
})
